import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import './styles.css';
import { Frame } from './frame.js';
import { t } from './i18n.js';
import { AdminAutocuePage, AdminIntegrationsPage } from './pages/admin.js';
import { AdminRolesPage, AdminUsersPage } from './pages/admin-users.js';
import { AdminImportPage } from './pages/admin-import.js';
import { AdminFeedsPage } from './pages/admin-feeds.js';
import { AdminAiPage } from './pages/admin-ai.js';
import { AdminKeysPage } from './pages/admin-keys.js';
import { FeedsPage } from './pages/feeds.js';
import { HomePage } from './pages/home.js';
import { AutocuePage } from './pages/autocue.js';
import { PlayoutControlPage } from './pages/playout-control.js';
import { ControlRoomPage, ControlRoomSelectPage } from './pages/control-room.js';
import { ArchivePage, DayListPage, QuickFindPage } from './pages/lists.js';
import { LoginPage } from './pages/login.js';
import { PasswordPage } from './pages/password.js';
import { RundownGridPage } from './pages/rundown-grid.js';
import { RundownSelectPage } from './pages/rundown-select.js';
import { StoryEditorPage } from './pages/story-editor.js';
import { StoryViewPage } from './pages/story-view.js';
import { TopicsPage } from './pages/topics.js';
import { VideoArchivePage } from './pages/archive.js';
import { VideoHubPage } from './pages/video-hub.js';
import { VideoEditorPage } from './pages/video-editor.js';
import { WebArticlePage } from './pages/web-article.js';
import { match, useLocation } from './router.js';
import { SessionProvider, useSession } from './session.js';
import { installTouchDrag } from './touch-drag.js';
import { realtime } from './ws.js';

installTouchDrag();

function NotBuiltPage({ title }: { title: string }) {
  return (
    <>
      <h1 className="page-title">{title}</h1>
      <div className="banner info">
        <b>STUB</b> Η ενότητα αυτή δεν έχει υλοποιηθεί στο AirPost (βλ. docs/open-questions.md Q9). Παραμένει διαθέσιμη στο HPress.
      </div>
    </>
  );
}

function Routes() {
  const { path } = useLocation();
  let p: Record<string, string> | null;
  if (path === '/' || path === '') return <HomePage />;
  if (path === '/rundown') return <RundownSelectPage />;
  if ((p = match('/rundown/:id', path))) return <RundownGridPage id={p.id!} />;
  if (path === '/story/new') return <StoryEditorPage id={null} />;
  if ((p = match('/story/:id/edit', path))) return <StoryEditorPage id={p.id!} />;
  if ((p = match('/story/:id', path))) return <StoryViewPage id={p.id!} />;
  if ((p = match('/edit/:id', path))) return <VideoEditorPage id={p.id!} />;
  if (path === '/stories/day') return <DayListPage />;
  if (path === '/stories/quick') return <QuickFindPage />;
  if (path === '/stories/archive') return <ArchivePage />;
  if (path === '/admin' || path === '/admin/autocue') return <AdminAutocuePage />;
  if (path === '/admin/integrations') return <AdminIntegrationsPage />;
  if (path === '/admin/users') return <AdminUsersPage />;
  if (path === '/admin/roles') return <AdminRolesPage />;
  if (path === '/admin/import') return <AdminImportPage />;
  if (path === '/admin/feeds') return <AdminFeedsPage />;
  if (path === '/admin/ai') return <AdminAiPage />;
  if (path === '/admin/keys') return <AdminKeysPage />;
  if (path === '/feeds' || path === '/feeds/') return <FeedsPage />;
  if ((p = match('/playout/:id', path))) return <PlayoutControlPage id={p.id!} />;
  if (path === '/control' || path === '/control/') return <ControlRoomSelectPage />;
  if ((p = match('/control/:id', path))) return <ControlRoomPage id={p.id!} />;
  if (path === '/autocue' || path === '/autocue/') return <AutocuePage />;
  if (path === '/password') return <PasswordPage />;
  if (path === '/topics' || path === '/topics/') return <TopicsPage key="topics" />;
  if (path === '/topics/new') return <TopicsPage key="topics-new" openNew />;
  if (path === '/video-hub') return <VideoHubPage />;
  if (path === '/video' || path === '/video/') return <VideoArchivePage key="videos" />;
  if (path === '/video/new') return <VideoArchivePage key="videos-new" openNew />;
  return <NotBuiltPage title="404" />;
}

function App() {
  const { session, loading } = useSession();
  const { path } = useLocation();
  useEffect(() => {
    if (session) realtime.connect();
    else realtime.disconnect();
  }, [session]);
  if (loading) return <div style={{ padding: 20 }}>{t('loading')}</div>;
  if (!session) return <LoginPage />;
  // the portal's page stands on its own, without AirPost's frame round it
  const web = match('/web/:id', path);
  if (web) return <WebArticlePage id={web.id!} />;
  return (
    <Frame>
      <Routes />
    </Frame>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <SessionProvider>
      <App />
    </SessionProvider>
  </StrictMode>,
);
